import { Formik, FormikHelpers } from 'formik';

import { TFunction } from 'i18next';
import { AuthAvailableTournament } from 'modules/auth/domain/interfaces/auth-available-tournament';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { isEmptyAddress } from 'modules/core/helpers/is-empty-address';
import { Pages } from 'modules/navigation/domain/enums/pages';
import organizerFormValidator from 'modules/organizer/domain/validators/organizer.validator';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Background, backgrounds } from 'shared/constants/backgrounds';
import { usePreventNavigation } from 'shared/hooks';
import { getAvailableTournamentTypes } from 'shared/utils';
import { CoverDropzone } from 'ud-form/components/dropzoneV2';
import UDFormInput from 'ud-form/components/input';
import UDInputPhone from 'ud-form/components/input/phone';
import UDFormTextarea from 'ud-form/components/input/textarea';
import UDFormLabel from 'ud-form/components/label';
import UDFormSelect, { UDFormAddressSelect } from 'ud-form/components/select';
import UDButton from 'ud-ui/components/button';
import {
  mapAddressToDadataArea,
  mapDadataAreaToAddress,
} from 'ud-ui/components/dadata-address-autocomplete/infra/mappers/dadata-area.mapper';
import { OrganizerUpsertPayload } from '../../../domain/interfaces/organizer-upsert-payload';

type SportTypeOption = {
  label: string;
  value: string;
};

type DadataAreaType = any; // Заменить на правильный тип, когда будет доступен

export interface OrganizerFormValues {
  name: string;
  sportType: SportTypeOption;
  email?: string;
  phone?: string;
  address?: DadataAreaType;
  description?: string;
  logo: {
    img: string | File;
    background: Background;
  }[];
}

const sportTypeToSelectOption = (sportType: string, t: TFunction): SportTypeOption => {
  const sportTypeKey = sportType.toLowerCase();
  return {
    label: t(`sportType.${sportTypeKey}`),
    value: sportTypeKey,
  };
};

const sportTypesToSelectOptions = (availableTournaments: AuthAvailableTournament[], t: TFunction): SportTypeOption[] =>
  getAvailableTournamentTypes(availableTournaments).map((sportType) => sportTypeToSelectOption(sportType, t));

type OrganizerFormProps = {
  isLoading?: boolean;
  initialValues?: OrganizerUpsertPayload & { backgroundColor?: Background };
  onSubmit: (values: OrganizerUpsertPayload, formikHelpers: FormikHelpers<OrganizerFormValues>) => void;
};

export const OrganizerForm = ({ isLoading, initialValues, onSubmit }: OrganizerFormProps) => {
  const { t } = useTranslation();

  // TODO: add filter by available sport type
  const sportTypeOptions = sportTypesToSelectOptions([], t);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const initialFormValues: OrganizerFormValues = initialValues
    ? {
        ...initialValues,
        logo: [
          {
            img: initialValues.logoUrl ?? '',
            background: initialValues.backgroundColor ?? backgrounds[0],
          },
        ],
        address: isEmptyAddress(initialValues.address) ? undefined : mapAddressToDadataArea(initialValues.address!),
        sportType: initialValues.sportType
          ? sportTypeToSelectOption(initialValues.sportType, t)
          : sportTypeToSelectOption(SportType.football, t),
      }
    : {
        name: '',
        sportType: sportTypeToSelectOption(SportType.football, t),
        phone: '',
        logo: [{ img: '', background: backgrounds[0] }],
      };

  const handleSubmit = useCallback(
    (values: OrganizerFormValues, formikHelpers: FormikHelpers<OrganizerFormValues>) => {
      const { address: changedAddress } = values;
      const address = changedAddress ? mapDadataAreaToAddress(changedAddress) : changedAddress;

      const data = {
        ...values,
        address,
        sportType: values.sportType.value,
      };

      onSubmit(data as OrganizerUpsertPayload, formikHelpers);
    },
    [onSubmit],
  );

  const { PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.TOURNEYS.INDEX,
  });

  return (
    <Formik<OrganizerFormValues>
      enableReinitialize
      initialValues={initialFormValues}
      validationSchema={organizerFormValidator(t)}
      onSubmit={(values, formikHelpers) => {
        handleSubmit(values, formikHelpers);
        formikHelpers.resetForm({ values }); // делаем текущие значения базой
        setIsFormDirty(false);
      }}
      validateOnMount
    >
      {(formProps) => {
        setIsFormDirty(formProps.dirty);
        return (
          <form onSubmit={formProps.handleSubmit}>
            <div className="row g-2">
              <div className="col-6">
                <UDFormInput
                  disabled={isLoading}
                  name="name"
                  label="Название организации*"
                  placeholder="Введите название"
                  containerProps={{ className: 'mb-5' }}
                />

                <UDFormSelect
                  isDisabled={isLoading}
                  name="sportType"
                  label="Тип спорта"
                  placeholder="Выберите тип спорта"
                  isMulti={false}
                  options={sportTypeOptions}
                  containerProps={{ className: 'mb-5' }}
                />

                <UDFormInput
                  disabled={isLoading}
                  name="email"
                  label="Email"
                  placeholder="Email"
                  containerProps={{ className: 'mb-5' }}
                />

                <UDInputPhone
                  name="phone"
                  label="Телефон"
                  placeholder="Введите номер телефона"
                  wrapperProps={{ className: 'mb-6' }}
                  value={formProps.values.phone}
                />

                <UDFormAddressSelect
                  name="address"
                  label="Город"
                  placeholder={t('championship.info.addressInput.placeholder')}
                  containerProps={{ className: 'mb-5' }}
                  onlyCity={true}
                />

                <UDFormTextarea
                  disabled={isLoading}
                  name="description"
                  label="Описание"
                  placeholder="Введите текст"
                  containerProps={{ className: 'mb-5' }}
                />
              </div>
              <div className="col-6">
                <UDFormLabel className="mb-2">Обложка и изображение</UDFormLabel>
                <CoverDropzone name="logo" className="p-6" />
              </div>
            </div>
            <div className="d-flex justify-content-end mb-2 mt-6">
              <UDButton
                variant="primary"
                type="submit"
                className="mb-3"
                disabled={isLoading || !formProps.dirty || !formProps.isValid}
                loading={isLoading || formProps.isSubmitting}
              >
                Сохранить
              </UDButton>
            </div>

            <PreventNavigationModal />
          </form>
        );
      }}
    </Formik>
  );
};
